"use client";

import { Switch } from "@dafiti/closet-ui";
import { AddToCart, Check, FreightLarge, Full } from "@dafiti/closet-ui/icons";
import type { ComponentType, SVGProps } from "react";

import type { FilterParamKey } from "../../../hooks/useFilterParams.hook";
import { useFilterParams } from "../../../hooks/useFilterParams.hook";
import { useFiltersQuery } from "../../../hooks/useFiltersQuery.hook";
import { catalogFilterClick } from "../../../tracking/catalogPage.tracking";
import type { DefaultBooleanFacet } from "../../../types/filters.type";

const FIELD_ICON: Partial<
  Record<FilterParamKey, ComponentType<SVGProps<SVGSVGElement>>>
> = {
  enable_free_shipping: FreightLarge,
  shipping_type: Full,
};

type BooleanQuickFilterProps = {
  facet: DefaultBooleanFacet;
};

function BooleanQuickFilter({ facet }: BooleanQuickFilterProps) {
  const { isFilterActive } = useFiltersQuery();
  const [, setFilterParams] = useFilterParams();

  const active = isFilterActive(facet.field);

  const setValue = (next: boolean) => {
    (setFilterParams as (v: Record<string, boolean | null>) => void)({
      [facet.field]: next || null,
    });
    if (next) catalogFilterClick([facet.field]);
  };

  const FieldIcon = FIELD_ICON[facet.field as FilterParamKey] ?? null;

  const pillClasses = active
    ? "border-positive-400 bg-positive-100 text-positive-400"
    : "border-gray-600 bg-transparent text-gray-600";

  return (
    <>
      {/* Mobile: pill button */}
      <button
        type="button"
        onClick={() => setValue(!active)}
        aria-pressed={active}
        aria-label={facet.label}
        data-testid={`boolean-quick-filter-${facet.field}`}
        className={`inline-flex h-2xs items-center justify-center gap-nano rounded-full border px-3xs text-caption transition lg:hidden ${pillClasses}`}
      >
        {FieldIcon ? (
          <FieldIcon className="h-auto w-3xs" aria-hidden="true" />
        ) : null}
        <span className="text-caption">{facet.label}</span>
        {active ? (
          <Check className="h-[7px] w-[10px]" aria-hidden="true" />
        ) : (
          <AddToCart className="size-nano" aria-hidden="true" />
        )}
      </button>

      {/* Desktop: icon + label + switch */}
      <div
        className="hidden lg:inline-flex items-center gap-nano"
        data-testid={`boolean-quick-filter-desktop-${facet.field}`}
      >
        {FieldIcon ? (
          <FieldIcon className="size-2xs text-gray-600" aria-hidden="true" />
        ) : null}
        <span className="text-body-2 text-gray-600 whitespace-nowrap">
          {facet.label}
        </span>
        <Switch
          className="not-disabled:data-[state=checked]:bg-positive-200"
          checked={active}
          onCheckedChange={setValue}
          aria-label={facet.label}
        />
      </div>
    </>
  );
}

export { BooleanQuickFilter };
export type { BooleanQuickFilterProps };
